import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import NavigationService from '@/Management/NavigationService';
import NavigationHelper from '@/Management/NavigationHelper';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import GenericObject from '@/Model/GenericObject';
import * as DTOs from "coreframetypings/dto";
import Loader from '@/Utility/Loader';
import LocatorHelper from '@/Utility/LocatorHelper';
import { IDataGridViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';

import AssociationGridFactory from '@/Factories/ControlFactories/AssociationGridFactory';
import DataGridViewModelType from '@/ViewModels/Controls/DataGridViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import Utilities from '@/Utility/Utilities';

export default class IndirectAssociationGridFactory extends AssociationGridFactory {

    public dataGrid: DataGridViewModelType; // initialized by baseclass
    protected viewFactorySearchListView; // disposed by baseclass

    protected associationName: string;
    protected associationPdoId: KnockoutObservable<string>;
    protected associationType: string;
    protected associationListViewName: string;
    protected associationConnectOperationName: string;
    protected associationDisconnectOperationName: string;
    protected associationContextParameterName: string;
    protected controlDomElement: JQuery = $("<div>");

    constructor(protected params: any) {
        super(params);
        
        this.labelPosition = this.paramOrDefault(params.labelPosition, 'Top');
        this.colspan = this.paramOrDefault(params.colspan, 'Two');
    }

    /**
     * @override
     * @inheritdoc
     */
    public getControl(): JQuery {
        this.getListView().then((listViewDto) => {
            if (this.validateStatus(listViewDto)) {
                this.updateControlDomElement(listViewDto);
            }
        });

        return this.controlDomElement;
    }

    /**
     * @override
     * @inheritdoc 
     */
    public createDataGridViewModelParameter(): IDataGridViewModelConstructorParameter {
        const params = super.createDataGridViewModelParameter();
        params.associationName = this.associationName;
        params.associationPdoId = this.associationPdoId();
        params.associationType = this.associationType;
        params.height = '200px';
        params.onDoubleClick = (pdoId: string, rowData: any, openInNewWindow: boolean) => {
            const isEditButtonHidden = !this.detailViewItem.listView.showButtonEdit;
            const isNavigationDisabled = this.detailViewItem.disableNavigation == pdoId;

            if (isEditButtonHidden || isNavigationDisabled) {
                return;
            }


            this.navigateToListView(pdoId, openInNewWindow);
        };
        params.historyDate = this.getGenericObject().historizationDate;
        params.showHistoricedObjects = this.getGenericObject().isHistorized;
        return params;
    }

    /**
     * @override
     * @inheritdoc 
     */
    public createToolBarViewModel(): ToolBarViewModel {
        let toolbar = super.createToolBarViewModel();

        let newAndConnectLink = this.getNewAndConnectLink(toolbar);
        let editConnectedLink = this.getEditConnectedLink(toolbar);
        let findAndConnectLink = this.getFindAndConnectLink(toolbar);
        let disconnectLinkViewModel = this.getDisconnectLink(toolbar);

        this.overrideOnClick(newAndConnectLink, this.onNewAndConnect);
        this.overrideOnClick(editConnectedLink, this.onEditConnected);
        this.overrideOnClick(findAndConnectLink, this.onFindAndConnect);
        this.overrideOnClick(disconnectLinkViewModel, this.onDisconnectItem);

        return toolbar;
    }

    protected overrideOnClick(linkViewModel: LinkViewModel, clickHandler: () => void): void {
        linkViewModel.onClick = () => {
            clickHandler.apply(this);
        };
    }

    protected onNewAndConnect(): void {
        let callee = this;
        let typeName = this.detailViewItem.listView.typeName;
        let pdoId = null;
        let contextPdoId = this.getGenericObject().pdoId;
        let contextPdTypeName = this.getGenericObject().pdTypeName;
        let contextRelationName = null;
        let contextParameters = {};
        contextParameters[this.associationContextParameterName] = this.associationPdoId();
        let callId = null;

        let detailViewViewModel = this.context.viewModel;
        detailViewViewModel.createContext(
            callee,
            typeName,
            pdoId,
            contextPdoId,
            contextPdTypeName,
            contextRelationName,
            contextParameters,
            callId
        );
    }

    protected onEditConnected(): void {
        let genericObject = this.getGenericObject();
        let pdoId = this.dataGrid.selected()[0];
        let isModified = genericObject.isModified();

        if (isModified) {
            this.showSaveWarningMessageBox();
        }
        else {
            this.navigateToListView(pdoId);
        }
    }

    protected showSaveWarningMessageBox(): void {
         RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createSaveWarningMessageBox(this.saveWarningYesCallback, this.saveWarningNoCallback));
    }

    protected saveWarningYesCallback(): void {
        this.context.apply();
    }

    protected saveWarningNoCallback() {
        
    }

    protected onFindAndConnect(): void {
        let name = 'SearchListView';
        let params = {
            viewModel: this.createSearchListViewViewModel()
        };
        let loadTuple = true;

        this.viewFactorySearchListView = NavigationService.getInstance().loadViewFactoryIsolated(name, params, loadTuple);
    }

    protected createSearchListViewViewModel(): any {
        let SearchListViewViewModel = ComponentManager.getViewModel('SearchListViewViewModel') as any;

        let callback = (toPDOIDs) => {
            this.callConnect(toPDOIDs)
                .then((connectResult) => {
                    this.handleConnectResult(connectResult);
                });
        };

        let params = {
            parentLocator: this.locator,
            locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'search-view'),
            contextPdoId: this.context.genericObject.pdoId,
            listView: this.detailViewItem.listView,
            allowMultiSelect: true,
            callback: (toPDOIDs) => { callback(toPDOIDs); },
            onClose: () => { NavigationService.getInstance().cleanRegion('Modal'); }
        };

        return new SearchListViewViewModel(params);
    }

    protected callConnect(toPDOIDs): JQueryDeferred<DTOs.IOperationResultDTO> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: this.associationConnectOperationName,
            operationParameters: [toPDOIDs, this.associationPdoId()],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: this.context.getNestedContextPath(),
            withValidation: false
        });
    }

    protected handleConnectResult(result): void {
        if (this.validateStatus(result)) {
            this.dataGrid.refresh();
            this.context.genericObject.isModified(true);
        }
    }

    protected onDisconnectItem(): void {
        Loader.getInstance().show();

        let genericObject = this.getGenericObject();
        let toPDOIDs = this.dataGrid.selected();
      
        this.callDisconnect(toPDOIDs, this.associationPdoId())
            .done((changeObject) => {
                if (!this.isDisposed) {
                    this.handleDisconnectResult(changeObject);    
                }
            })
            .always(() => {
                Loader.getInstance().hide();
            });
    }

    protected callDisconnect(toPDOIDs, pdoIds): JQueryDeferred<DTOs.IOperationResultDTO> {
        return RADEnvironmentProxy.getInstance().callOperationFromDVContext({
            detailViewContextID: this.context.id,
            operationName: this.associationDisconnectOperationName,
            operationParameters: [
                toPDOIDs,
                pdoIds
            ],
            property_values: JSON.stringify({}),
            contextParameters: {},
            nestedContextName: this.context.getNestedContextPath(),
            withValidation: false
        });
    }

    protected handleDisconnectResult(changeObject: DTOs.IOperationResultDTO): void {
        this.dataGrid.selected([]);

        if (this.validateStatus(changeObject)) {
            this.dataGrid.refresh();
            this.getGenericObject().isModified(true);
        }
    }

    public navigateToListView(pdoId: string, openInNewWindow: boolean = false): void {
        const principalFilterMode = this.dataGrid.principalFilterMode();
        const staticFilter = this.dataGrid.staticFilter();

        const optionalParameters = {
            contextSource: this.context.isSummaryViewMode() ? 'SV' : 'DV',
            contextTypeName: this.associationType,
            contextObject: this.associationPdoId(),
            contextRelation: this.associationName,
            principalFilterMode: principalFilterMode,
            preFilter: staticFilter,
        };

        NavigationHelper.navigateAssociation(this.detailViewItem.listView.typeName,
            pdoId, optionalParameters, openInNewWindow);
    }

    private getNewAndConnectLink(toolbar: ToolBarViewModel): LinkViewModel {
        return this.getLinkByLocatorSuffix(toolbar, 'toolbar.create');
    }

    private getEditConnectedLink(toolbar: ToolBarViewModel): LinkViewModel {
        return this.getLinkByLocatorSuffix(toolbar, 'toolbar.edit');
    }

    private getFindAndConnectLink(toolbar: ToolBarViewModel): LinkViewModel {
        return this.getLinkByLocatorSuffix(toolbar, 'toolbar.select');
    }

    private getDisconnectLink(toolbar: ToolBarViewModel): LinkViewModel {
        return this.getLinkByLocatorSuffix(toolbar, 'toolbar.disconnect');
    }

    private getLinkByLocatorSuffix(toolbar: ToolBarViewModel, locatorSuffix: string): LinkViewModel {
        return _.find(toolbar.primaryLinks, (link) => {
            let locator = Utilities.isFunction(link.locator) ? link.locator() : link.locator;
            return this.endsWith(locator, locatorSuffix);
        }) || null;
    }

    private endsWith(str: string, suffix: string): boolean {
        return str !== undefined && str !== null &&
            str.indexOf(suffix, str.length - suffix.length) !== -1;
    }

    private updateControlDomElement(listViewDto: DTOs.IListViewDTO): void {
        this.detailViewItem.listView = listViewDto;

        this.updateListView();
        this.updateToolbarConfiguration();

        let baseControlDomElement = super.getControl();

        this.replaceControlDomElement(baseControlDomElement);
    }

    protected updateListView(): void {
        this.detailViewItem.listView.isAssoziationListView = true;
    }

    protected updateToolbarConfiguration(): void {
        let listView = this.detailViewItem.listView;
        let canCreateAndConnect = this.hasTypes(listView);

        listView.showButtonCreate = canCreateAndConnect;
        listView.showButtonDisconnect = canCreateAndConnect;
        listView.showButtonSelect = canCreateAndConnect;
        listView.showButtonDelete = false;
        listView.showButtonXLSExport = false;
        listView.showButtonCSVExport = false;
        listView.showButtonEdit = true;
    }

    protected replaceControlDomElement(baseControlDomElement): void {
        window.setTimeout(() => {
            this.controlDomElement.replaceWith(baseControlDomElement);
        }, 250);
    }

    protected hasTypes(listView: { types: { length: number } }): boolean {
        return listView.types && listView.types.length > 0;
    }

    protected getListView(): JQueryDeferred<DTOs.IListViewDTO> {
        return RADEnvironmentProxy.getInstance().getListViewByName({
            listViewName: this.associationListViewName
        });
    }

    protected getGenericObject(): GenericObject {
        let context = this.context;
        let genericObject = context.genericObject;

        return genericObject;
    }

    protected getObservable<T>(genericObject: GenericObject, identifier: string): KnockoutObservable<T> {
        return genericObject.getObservable(identifier);
    }
}

ComponentManager.setFactory('IndirectAssociationGrid', IndirectAssociationGridFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');